// client/src/pages/PublicHomePage.js
import React from 'react';
import { Link } from 'react-router-dom';
import usePublicOffice, { illinoisContact } from '../hooks/usePublicOffice';
import { contactLines, IL_CONTACT_FALLBACK } from '../components/public/PublicContact';

// Florida's compliance and contact columns, from the portal's own data (audit 2026-10,
// H8). The Illinois columns below are unchanged.
// The office's public numbers and emails (Virtual Office Settings), one heading each.
const CONTACT_HEADING = { Main: 'Main phone', Secondary: 'Secondary phone', Email: 'Email', Office: 'Office email' };
const ContactBlocks = ({ contact }) => contactLines(contact).map((l) => (
    <div key={l.label}>
        <h4 className="font-semibold text-slate-800 text-lg">{CONTACT_HEADING[l.label]}:</h4>
        <p><a href={l.href} className="text-indigo-600 hover:underline text-base">{l.value}</a></p>
    </div>
));

const FloridaColumns = ({ jurisdiction, office }) => {
    const mb = office && office.managing_broker;
    return (
        <div className="grid lg:grid-cols-5 gap-12 items-start">
            <div className="lg:col-span-3 bg-slate-100 p-10 rounded-lg border border-slate-200">
                <h2 className="text-3xl font-bold text-slate-900 mb-6">Brokerage Compliance</h2>
                <div className="prose prose-slate max-w-none text-base">
                    <p>
                        Business Real Estate Corp. is a licensed real estate brokerage operating under {(jurisdiction && jurisdiction.statute) || 'Florida law'}.
                    </p>
                    <p>
                        All transaction records, escrow records and brokerage agreements are securely maintained and available on request to the {(jurisdiction && jurisdiction.regulator) || 'Division of Real Estate'}. Our public registry lists every sponsored licensee.
                    </p>
                    {office && office.physical_address && (
                        <div className="mt-8 border-t border-slate-300 pt-6">
                            <h4 className="font-semibold text-slate-800">Office:</h4>
                            <address className="not-italic text-slate-600 mt-2">Business Real Estate Corp.<br/>{office.physical_address}</address>
                        </div>
                    )}
                </div>
            </div>
            <div className="lg:col-span-2 bg-indigo-50 p-10 rounded-lg border border-indigo-200">
                <h2 className="text-3xl font-bold text-slate-900 mb-6">Contact Information</h2>
                {mb ? (
                    <div className="space-y-6">
                        <div><h4 className="font-semibold text-slate-800 text-lg">Broker:</h4><p className="text-slate-700 text-base">{mb.name}</p></div>
                        {mb.license_number && <div><h4 className="font-semibold text-slate-800 text-lg">License #:</h4><p className="text-slate-700 text-base">{mb.license_number}</p></div>}
                        {contactLines(office.contact).length ? <ContactBlocks contact={office.contact} /> : (
                            <>
                                {mb.email && <div><h4 className="font-semibold text-slate-800 text-lg">Email:</h4><p><a href={`mailto:${mb.email}`} className="text-indigo-600 hover:underline text-base">{mb.email}</a></p></div>}
                                {mb.phone && <div><h4 className="font-semibold text-slate-800 text-lg">Phone:</h4><p className="text-slate-700 text-base">{mb.phone}</p></div>}
                            </>
                        )}
                    </div>
                ) : <p><Link to="/contact" className="text-indigo-600 hover:underline">Contact us</Link></p>}
            </div>
        </div>
    );
};

const PublicHomePage = () => {
    const { isFL, jurisdiction, office } = usePublicOffice();
    // O8: Illinois facts from Virtual Office Settings. "Street, City, ST ZIP" prints as two lines.
    const pc = illinoisContact(office);
    const addr = String(pc.public_service_address || '');
    const cut = addr.indexOf(',');
    const addressLines = (cut > 0 ? [addr.slice(0, cut), addr.slice(cut + 1)] : [addr]).map((l) => l.trim()).filter(Boolean);
    return (
    <>
        {/* Hero Section */}
        <section className="bg-white">
            {/* Removed 'container' and 'mx-auto' for full width */}
            <div className="px-6 text-center py-28 lg:py-40">
                <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-slate-900 tracking-tight">
                    Your Partner in Commercial Real Estate
                </h1>
                {/* Note: This paragraph retains max-w-3xl and mx-auto to keep the text line length readable and centered within the full-width section */}
                <p className="mt-6 text-lg md:text-xl text-slate-700 max-w-3xl mx-auto">
                    Unlocking value through expertise, integrity, and unparalleled market knowledge across {isFL ? ((jurisdiction && jurisdiction.name) || 'Florida') : 'Illinois'}.
                </p>
                <Link 
                    to="/properties" 
                    className="mt-12 inline-block px-10 py-4 text-lg font-semibold text-white bg-slate-800 rounded-lg shadow-lg hover:bg-slate-900 transition-transform transform hover:scale-105"
                >
                    Search Properties
                </Link>
            </div>
        </section>

        {/* Core Services Section */}
        <section id="services" className="py-28 bg-slate-50 border-y border-slate-200">
            {/* Removed 'container' and 'mx-auto' for full width */}
            <div className="px-6 text-center">
                <h2 className="text-4xl font-bold text-slate-900 mb-20">Our Core Services</h2>
                {/* This grid will now span the full width (minus padding) */}
                <div className="grid md:grid-cols-3 gap-10">
                    <div className="bg-white p-8 rounded-xl shadow-lg hover:shadow-xl transition-shadow duration-300">
                        <h3 className="text-2xl font-bold text-slate-800 mb-4">Sales & Leasing</h3>
                        <p className="text-slate-600 leading-relaxed">
                            Expert representation for the acquisition, disposition, and leasing of commercial properties, from office and retail to industrial spaces.
                        </p>
                    </div>
                    <div className="bg-white p-8 rounded-xl shadow-lg hover:shadow-xl transition-shadow duration-300">
                        <h3 className="text-2xl font-bold text-slate-800 mb-4">Property Management</h3>
                        <p className="text-slate-600 leading-relaxed">
                            Comprehensive management services designed to maximize asset value and ensure seamless, compliant operations for property owners.
                        </p>
                    </div>
                    <div className="bg-white p-8 rounded-xl shadow-lg hover:shadow-xl transition-shadow duration-300">
                        <h3 className="text-2xl font-bold text-slate-800 mb-4">Consulting & Advisory</h3>
                        <p className="text-slate-600 leading-relaxed">
                            Strategic advice and market analysis to support your real estate investment decisions and long-term portfolio growth.
                        </p>
                    </div>
                </div>
            </div>
        </section>

        {/* Compliance & Contact Section */}
        <section id="compliance" className="py-28 bg-white">
            {/* Removed 'container' and 'mx-auto' for full width */}
            <div className="px-6">
                {isFL ? <FloridaColumns jurisdiction={jurisdiction} office={office} /> : (
                <div className="grid lg:grid-cols-5 gap-12 items-start">
                    {/* Virtual Office Compliance Column */}
                    <div className="lg:col-span-3 bg-slate-100 p-10 rounded-lg border border-slate-200">
                        <h2 className="text-3xl font-bold text-slate-900 mb-6">Virtual Office Compliance</h2>
                        <div className="prose prose-slate max-w-none text-base">
                            <p>
                                Business Real Estate Corp. is a licensed virtual real estate brokerage operating in full compliance with the Illinois Real Estate License Act.
                            </p>
                            <p>
                                Our digital infrastructure facilitates all brokerage services, communications, and secure document storage. All transaction records, escrow records, and brokerage agreements are securely maintained and accessible upon request by the Division. Our secure broker portal displays a current registry of all sponsored licensees and their respective managing brokers.
                            </p>
                            <div className="mt-8 border-t border-slate-300 pt-6">
                                <h4 className="font-semibold text-slate-800">Physical Address for Service of Process:</h4>
                                <address className="not-italic text-slate-600 mt-2">
                                    Business Real Estate Corp.<br/>
                                    {addressLines.map((l) => <React.Fragment key={l}>{l}<br/></React.Fragment>)}
                                </address>
                            </div>
                        </div>
                    </div>

                    {/* Contact Information Column */}
                    <div className="lg:col-span-2 bg-indigo-50 p-10 rounded-lg border border-indigo-200">
                        <h2 className="text-3xl font-bold text-slate-900 mb-6">Contact Information</h2>
                        <div className="space-y-6">
                            <div>
                                <h4 className="font-semibold text-slate-800 text-lg">Managing Broker:</h4>
                                <p className="text-slate-700 text-base">{pc.public_broker_name}</p>
                            </div>
                            <div>
                                <h4 className="font-semibold text-slate-800 text-lg">License #:</h4>
                                <p className="text-slate-700 text-base">{pc.public_broker_license}</p>
                            </div>
                            <ContactBlocks contact={office && contactLines(office.contact).length ? office.contact : IL_CONTACT_FALLBACK} />
                        </div>
                    </div>
                </div>
                )}
            </div>
        </section>
    </>
    );
};

export default PublicHomePage;